import React, { Suspense, useEffect, Component } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Link, useLocation, useNavigate } from 'react-router-dom';
import toast, { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';
import Layout from './components/layout/Layout';
import AdminLayout from './components/layout/AdminLayout';
import Home from './pages/Home';

class ErrorBoundary extends Component<
  { children: React.ReactNode; resetKey?: string },
  { hasError: boolean }
> {
  constructor(props: any) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() { return { hasError: true }; }
  componentDidUpdate(prevProps: { resetKey?: string }) {
    if (prevProps.resetKey !== this.props.resetKey && this.state.hasError) {
      this.setState({ hasError: false });
    }
  }
  componentDidCatch(error: Error, info: React.ErrorInfo) {
    if (process.env.NODE_ENV === 'development') {
      console.error('[ErrorBoundary]', error, info);
    }
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col items-center justify-center min-h-screen bg-carbon-10 px-4 text-center">
          <h1 className="text-2xl font-semibold text-carbon-100 mb-2">Something went wrong</h1>
          <p className="text-sm text-carbon-50 mb-6">An unexpected error occurred. Please reload to continue.</p>
          <button
            onClick={() => { this.setState({ hasError: false }); window.location.reload(); }}
            className="px-5 py-2.5 bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 transition-colors"
          >
            Reload Page
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

const Login = React.lazy(() => import('./pages/Auth/Login'));
const OAuth2Callback = React.lazy(() => import('./pages/Auth/OAuth2Callback'));

const Dashboard = React.lazy(() => import('./pages/Dashboard'));

const ProjectsList = React.lazy(() => import('./pages/Projects/ProjectsList'));
const ProjectDetail = React.lazy(() => import('./pages/Projects/ProjectDetail'));
const CreateProject = React.lazy(() => import('./pages/Projects/CreateProject'));
const EditProject = React.lazy(() => import('./pages/Projects/EditProject'));

const DonationForm = React.lazy(() => import('./pages/Donations/DonationForm'));
const DonationReceipt = React.lazy(() => import('./pages/Donations/DonationReceipt'));
const MyDonations = React.lazy(() => import('./pages/Donations/MyDonations'));
const MySubscriptions = React.lazy(() => import('./pages/Donations/MySubscriptions'));

const ReceiptsList = React.lazy(() => import('./pages/Receipts/ReceiptsList'));
const Profile = React.lazy(() => import('./pages/Profile/Profile'));

const AdminDashboard = React.lazy(() => import('./pages/Admin/AdminDashboard'));
const UsersManagement = React.lazy(() => import('./pages/Admin/UsersManagement'));
const DonationsManagement = React.lazy(() => import('./pages/Admin/DonationsManagement'));
const AmbassadorsManagement = React.lazy(() => import('./pages/Admin/AmbassadorsManagement'));
const AlumniRequestsManagement = React.lazy(() => import('./pages/Admin/AlumniRequestsManagement'));

const AmbassadorDashboard = React.lazy(() => import('./pages/Ambassador/AmbassadorDashboard'));

const PaymentFailure = React.lazy(() => import('./pages/PaymentFailure'));

const RouteFallback: React.FC = () => (
  <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50/50">
    <div className="text-center">
      <div className="relative w-20 h-20 mx-auto mb-6">
        <div className="absolute inset-0 border-4 border-primary-200 rounded-full"></div>
        <div className="absolute inset-0 border-4 border-primary-600 rounded-full border-t-transparent animate-spin"></div>
      </div>
      <p className="text-lg font-bold text-gray-600 animate-pulse">Loading Experience...</p>
      <p className="text-xs text-gray-400 mt-2 uppercase tracking-widest">Please wait</p>
    </div>
  </div>
);

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50/50">
        <div className="text-center">
          <div className="relative w-16 h-16 mx-auto mb-4">
            <div className="absolute inset-0 border-4 border-primary-200 rounded-full"></div>
            <div className="absolute inset-0 border-4 border-primary-600 rounded-full border-t-transparent animate-spin"></div>
          </div>
          <p className="text-sm font-bold text-gray-500 animate-pulse">Authenticating...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }

  return <Layout>{children}</Layout>;
};

const AmbassadorRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isAmbassador, isAdmin, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50/50">
        <div className="text-center">
          <div className="relative w-16 h-16 mx-auto mb-4">
            <div className="absolute inset-0 border-4 border-primary-200 rounded-full"></div>
            <div className="absolute inset-0 border-4 border-primary-600 rounded-full border-t-transparent animate-spin"></div>
          </div>
          <p className="text-sm font-bold text-gray-500 animate-pulse">Verifying Access...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }


  const hasAmbassadorAccess = isAmbassador || isAdmin;

  if (!hasAmbassadorAccess) {
    return <Navigate to="/dashboard" replace />;
  }

  return <Layout>{children}</Layout>;
};

const AdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isAdmin, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#0d1117]">
        <div className="text-center">
          <div className="w-8 h-8 mx-auto mb-4 border-2 border-primary-500 border-t-transparent animate-spin" />
          <p className="text-sm text-[#8b949e] animate-pulse">Verifying access...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  return <AdminLayout>{children}</AdminLayout>;
};

const GuestRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50/50">
        <div className="text-center">
          <div className="relative w-16 h-16 mx-auto mb-4">
            <div className="absolute inset-0 border-4 border-primary-200 rounded-full"></div>
            <div className="absolute inset-0 border-4 border-primary-600 rounded-full border-t-transparent animate-spin"></div>
          </div>
          <p className="text-sm font-bold text-gray-500 animate-pulse">Loading...</p>
        </div>
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};

function AppRoutes() {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail) toast.error(detail);
    };
    window.addEventListener('toast:error', handler);
    return () => window.removeEventListener('toast:error', handler);
  }, []);

  useEffect(() => {
    const codeParam = new URLSearchParams(location.search).get('code');
    if (
      codeParam &&
      location.pathname !== '/callback' &&
      location.pathname !== '/oauth2/callback'
    ) {
      navigate(`/callback${location.search}`, { replace: true });
    }
  }, [location.search, location.pathname, navigate]);

  return (
    <>
      <Toaster
        position="top-right"
        gutter={8}
        containerStyle={{ top: 72 }}
        toastOptions={{
          duration: 4000,
          style: {
            background: '#1f2328',
            color: '#f0f6fc',
            fontSize: '13px',
            fontFamily: "'Inter', system-ui, sans-serif",
            fontWeight: 500,
            borderRadius: '12px',
            padding: '12px 16px',
            maxWidth: 380,
            boxShadow: '0 8px 24px rgba(0,0,0,.22), 0 2px 6px rgba(0,0,0,.12)',
            letterSpacing: '0.01em',
            lineHeight: '1.5',
          },
          success: {
            duration: 3500,
            style: {
              background: '#0f2d1f',
              color: '#6ee7b7',
              border: '1px solid rgba(110,231,183,.15)',
            },
            iconTheme: {
              primary: '#10b981',
              secondary: '#0f2d1f',
            },
          },
          error: {
            duration: 5000,
            style: {
              background: '#2d0f0f',
              color: '#fca5a5',
              border: '1px solid rgba(252,165,165,.15)',
            },
            iconTheme: {
              primary: '#ef4444',
              secondary: '#2d0f0f',
            },
          },
          loading: {
            style: {
              background: '#1f2328',
              color: '#e2e8f0',
            },
          },
        }}
      />
      <Suspense fallback={<RouteFallback />}>
        <Routes>

          <Route path="/" element={<Layout><Home /></Layout>} />

          <Route
            path="/login"
            element={
              <GuestRoute>
                <Login />
              </GuestRoute>
            }
          />
          <Route path="/callback" element={<OAuth2Callback />} />
          <Route path="/oauth2/callback" element={<OAuth2Callback />} />

          <Route path="/payment-failure" element={<Layout><PaymentFailure /></Layout>} />
          <Route path="/payment-success" element={<Navigate to="/donations/my-donations" replace />} />

          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />

          <Route path="/projects" element={<Layout><ProjectsList /></Layout>} />

          <Route
            path="/projects/create"
            element={
              <AdminRoute>
                <CreateProject />
              </AdminRoute>
            }
          />

          <Route path="/projects/:id" element={<Layout><ProjectDetail /></Layout>} />
          <Route
            path="/projects/:id/edit"
            element={
              <AdminRoute>
                <EditProject />
              </AdminRoute>
            }
          />

          <Route
            path="/donate"
            element={
              <ProtectedRoute>
                <DonationForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/projects/:projectId/donate"
            element={
              <ProtectedRoute>
                <DonationForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/donations/:id/receipt"
            element={
              <ProtectedRoute>
                <DonationReceipt />
              </ProtectedRoute>
            }
          />

          <Route
            path="/receipts"
            element={
              <ProtectedRoute>
                <ReceiptsList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/receipts/:id"
            element={
              <ProtectedRoute>
                <DonationReceipt />
              </ProtectedRoute>
            }
          />
          <Route
            path="/donations/my-donations"
            element={
              <ProtectedRoute>
                <MyDonations />
              </ProtectedRoute>
            }
          />

          <Route
            path="/my-donations"
            element={<Navigate to="/donations/my-donations" replace />}
          />

          <Route
            path="/my-subscriptions"
            element={
              <ProtectedRoute>
                <MySubscriptions />
              </ProtectedRoute>
            }
          />

          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <Profile />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin"
            element={
              <AdminRoute>
                <AdminDashboard />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/users"
            element={
              <AdminRoute>
                <UsersManagement />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/donations"
            element={
              <AdminRoute>
                <DonationsManagement />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/ambassadors"
            element={
              <AdminRoute>
                <AmbassadorsManagement />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/alumni-requests"
            element={
              <AdminRoute>
                <AlumniRequestsManagement />
              </AdminRoute>
            }
          />

          <Route
            path="/ambassador"
            element={
              <AmbassadorRoute>
                <AmbassadorDashboard />
              </AmbassadorRoute>
            }
          />

          <Route path="*" element={
            <Layout>
              <div className="flex flex-col items-center justify-center min-h-[60vh] px-4 text-center">
                <h1 className="text-6xl font-bold text-gray-200 mb-4">404</h1>
                <p className="text-lg text-gray-500 mb-8">Page not found</p>
                <Link to="/" className="px-6 py-3 bg-primary-600 text-white rounded-xl hover:bg-primary-700 transition-all font-medium">
                  Go Home
                </Link>
              </div>
            </Layout>
          } />
        </Routes>
      </Suspense>
    </>
  );
}

function AppWithErrorBoundary() {
  const location = useLocation();
  return (
    <ErrorBoundary resetKey={location.pathname}>
      <AppRoutes />
    </ErrorBoundary>
  );
}

function App() {
  return (
    <AuthProvider>
      <Router>
        <AppWithErrorBoundary />
      </Router>
    </AuthProvider>
  );
}

export default App;
